import { Flex, jwtDecodeSafely, localStorage, Spinner, Text, ViewerContext } from "dash-ui";
import { RoutesPath } from "flow-components/routes/routes";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthPagesLayout } from "v2/components/auth-pages-layout";
import { ConfirmMfaCodeForm } from "v2/components/auth/confirm-mfa-form";
import { EnableMfaForm } from "v2/components/auth/enable-mfa-form";

const MFA_TOKEN_MAX_DURATION_SECONDS = 600; // 10 minutes

export function MfaConfirmationPage() {
  // Hooks
  const auth = useContext(ViewerContext);
  const navigate = useNavigate();

  const [isMfaToken, setIsMfaToken] = useState(true);

  const localToken = localStorage.getAccessToken();
  const account = auth.account;

  const decodedToken = useMemo(() => {
    try {
      return jwtDecodeSafely(localToken || auth?.tokens?.accessToken);
    } catch (error) {
      console.error("Failed to decode token:", error);
      return null;
    }
  }, [localToken, auth?.tokens?.accessToken]);

  // Redirect unauthenticated users
  useEffect(() => {
    if (!localToken && !auth?.tokens?.accessToken) {
      navigate(RoutesPath.SignIn);
    }
  }, [localToken, auth?.tokens?.accessToken]);

  // Handle edge case: stuck in MFA with no way to proceed
  useEffect(() => {
    // Check if token is expired while on MFA page
    if (localToken && decodedToken?.exp) {
      const now = Math.floor(Date.now() / 1000);
      if (decodedToken.exp < now) {
        // MFA token expired - clear and redirect
        localStorage.setAccessToken("");
        navigate(RoutesPath.SignIn);
        return;
      }
    }
    
    // If we somehow have a refresh token but no temp token during MFA, clear everything
    const refreshToken = localStorage.getRefreshToken();
    if (!localToken && refreshToken && !account) {
      localStorage.setRefreshToken("");
      localStorage.setRefreshTokenExpiry("");
      navigate(RoutesPath.SignIn);
    }
  }, [localToken, decodedToken, account, navigate]);

  useEffect(() => {
    // Only navigate when we have an account
    // For MFA tokens, we stay on this page until account loads
    // For non-MFA tokens with an account, redirect immediately
    if (account) {
      navigate(RoutesPath.Properties);
    }
  }, [account]);

  useEffect(() => {
    // Identify the token type (MFA or access) based on the expiration timestamp
    if (decodedToken?.exp) {
      const now = Math.floor(Date.now() / 1000); // current Unix timestamp in seconds

      // We are using 10 minutes because the non-MFA token lasts much more than that:
      const mfaTokenExpirationTimestamp = now + MFA_TOKEN_MAX_DURATION_SECONDS;
      if (decodedToken.exp > mfaTokenExpirationTimestamp) {
        setIsMfaToken(false);
      }
    }
  }, [decodedToken]);

  // HTML Element
  return (
    <AuthPagesLayout>
      {account || !isMfaToken ? (
        <Flex
          justifyContent="center"
          alignItems="center"
          h="100%"
          flexDirection="column"
        >
          <Text lg>Loading account info, please wait...</Text>
          <Spinner mt={4} />
          <Flex mt={8} w={280}>
            <Text>
              <i>
                If you are not redirected in 5 seconds, please refresh the page
                or contact support.
              </i>
            </Text>
          </Flex>
        </Flex>
      ) : (
        <>
          {!decodedToken?.mfaEnabled && <EnableMfaForm />}
          {decodedToken?.mfaEnabled && <ConfirmMfaCodeForm />}
        </>
      )}
    </AuthPagesLayout>
  );
}
